import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { UserCircle, X, Loader2, Landmark, Users, Save, Globe, LayoutDashboard, Percent, Receipt, Radio } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import ChannelCommissionsDialog from "./ChannelCommissionsDialog";
import SubscriptionsDialog from "./SubscriptionsDialog";
import Portal from "./Portal";

const COUNTRIES = [
  { code: "IN", name: "India", currency: "INR" },
  { code: "US", name: "United States", currency: "USD" },
  { code: "GB", name: "United Kingdom", currency: "GBP" },
  { code: "AE", name: "UAE", currency: "AED" },
  { code: "SG", name: "Singapore", currency: "SGD" },
];

export default function AdminProfileMenu({ user, onOpenUserManagement, onOpenCRS, onOpenConsolidated }) {
  const [open, setOpen] = useState(false);
  const [taxOpen, setTaxOpen] = useState(false);
  const [commOpen, setCommOpen] = useState(false);
  const [subsOpen, setSubsOpen] = useState(false);
  const [healthOpen, setHealthOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <>
      <div ref={ref} className="relative">
        <button data-testid="admin-profile-btn" onClick={() => setOpen(!open)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-full hover:bg-slate-100 transition-colors">
          <UserCircle size={22} className="text-slate-700" />
          <span className="text-sm font-semibold text-slate-700 hidden sm:inline">{user?.name || "Admin"}</span>
        </button>
        {open && (
          <div data-testid="admin-profile-menu"
            className="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-24px)] max-h-[calc(100vh-96px)] overflow-y-auto overscroll-contain bg-white rounded-2xl shadow-xl border border-slate-200 py-2 z-50">
            <div className="px-4 py-3 border-b border-slate-100 sticky top-0 bg-white z-10">
              <p className="font-semibold text-sm text-slate-900">{user?.name || "Admin"}</p>
              <p className="text-xs text-slate-500 mt-0.5">{user?.email || ""}</p>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-[#4F46E5] mt-1">Admin · Cross-property control</p>
            </div>
            <button data-testid="admin-consolidated-btn" onClick={() => { onOpenConsolidated?.(); setOpen(false); }}
              className="w-full flex items-start gap-3 px-4 py-3 hover:bg-slate-50 text-left">
              <LayoutDashboard size={18} className="text-slate-500 mt-0.5" />
              <div>
                <p className="font-semibold text-sm text-slate-900">Consolidated View</p>
                <p className="text-[11px] text-slate-500 mt-0.5">Today's arrivals, departures, occupancy, revenue &amp; net across every property.</p>
              </div>
            </button>
            <button data-testid="admin-crs-btn" onClick={() => { onOpenCRS?.(); setOpen(false); }}
              className="w-full flex items-start gap-3 px-4 py-3 hover:bg-slate-50 text-left">
              <Globe size={18} className="text-slate-500 mt-0.5" />
              <div>
                <p className="font-semibold text-sm text-slate-900">CRS Reservations</p>
                <p className="text-[11px] text-slate-500 mt-0.5">Consolidated bookings feed across every property under this admin.</p>
              </div>
            </button>
            <button data-testid="admin-tax-btn" onClick={() => { setTaxOpen(true); setOpen(false); }}
              className="w-full flex items-start gap-3 px-4 py-3 hover:bg-slate-50 text-left">
              <Landmark size={18} className="text-slate-500 mt-0.5" />
              <div>
                <p className="font-semibold text-sm text-slate-900">Taxation</p>
                <p className="text-[11px] text-slate-500 mt-0.5">Country-based tax rules applied to every property under this account.</p>
              </div>
            </button>
            <button data-testid="admin-commissions-btn" onClick={() => { setCommOpen(true); setOpen(false); }}
              className="w-full flex items-start gap-3 px-4 py-3 hover:bg-slate-50 text-left">
              <Percent size={18} className="text-slate-500 mt-0.5" />
              <div>
                <p className="font-semibold text-sm text-slate-900">Channel Commissions</p>
                <p className="text-[11px] text-slate-500 mt-0.5">OTA commission % per channel — AI-refreshed monthly, used by the Revenue Report.</p>
              </div>
            </button>
            <button data-testid="admin-subscriptions-btn" onClick={() => { setSubsOpen(true); setOpen(false); }}
              className="w-full flex items-start gap-3 px-4 py-3 hover:bg-slate-50 text-left">
              <Receipt size={18} className="text-slate-500 mt-0.5" />
              <div>
                <p className="font-semibold text-sm text-slate-900">Subscription &amp; Invoices</p>
                <p className="text-[11px] text-slate-500 mt-0.5">Every property's PMS subscription, MRR, renewal &amp; invoice history. OTA-mapped units are billed here only.</p>
              </div>
            </button>
            <button data-testid="admin-users-btn" onClick={() => { onOpenUserManagement?.(); setOpen(false); }}
              className="w-full flex items-start gap-3 px-4 py-3 hover:bg-slate-50 text-left">
              <Users size={18} className="text-slate-500 mt-0.5" />
              <div>
                <p className="font-semibold text-sm text-slate-900">User Management</p>
                <p className="text-[11px] text-slate-500 mt-0.5">Add or revoke access. Assign users to specific properties only.</p>
              </div>
            </button>
            <button data-testid="admin-health-btn" onClick={() => { setHealthOpen(true); setOpen(false); }}
              className="w-full flex items-start gap-3 px-4 py-3 hover:bg-slate-50 text-left">
              <Radio size={18} className="text-slate-500 mt-0.5" />
              <div>
                <p className="font-semibold text-sm text-slate-900">Connectivity Health</p>
                <p className="text-[11px] text-slate-500 mt-0.5">Every property's OTA Connectivity sync log &amp; drift — one place for cross-property staff to catch issues.</p>
              </div>
            </button>
          </div>
        )}
      </div>
      {taxOpen && <TaxationDialog onClose={() => setTaxOpen(false)} />}
      {commOpen && <ChannelCommissionsDialog onClose={() => setCommOpen(false)} />}
      {subsOpen && <SubscriptionsDialog onClose={() => setSubsOpen(false)} />}
      {healthOpen && <HealthCenterDialog onClose={() => setHealthOpen(false)} />}
    </>
  );
}

function TaxationDialog({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/pms/admin/taxation").then(({ data }) => setCfg(data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  const updateSlab = (i, field, value) => {
    const slabs = [...cfg.slabs];
    slabs[i] = { ...slabs[i], [field]: value === "" ? null : parseFloat(value) };
    setCfg({ ...cfg, slabs });
  };
  const addSlab = () => setCfg({ ...cfg, slabs: [...cfg.slabs, { min: 0, max: null, rate: 0 }] });
  const removeSlab = (i) => setCfg({ ...cfg, slabs: cfg.slabs.filter((_, x) => x !== i) });
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/pms/admin/taxation", cfg);
      toast.success("Taxation applied to every property under this admin");
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const applyIndiaPreset = () => setCfg({
    country: "IN", currency: "INR",
    slabs: [{ min: 0, max: 1000, rate: 0 }, { min: 1000, max: 7500, rate: 5 }, { min: 7500, max: null, rate: 18 }],
  });

  return (
    <Portal>
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[80] grid place-items-center p-4 sm:p-6 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-[96vw] min-h-[92vh] max-h-[92vh] max-w-none shadow-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-white rounded-t-2xl border-b border-slate-100 px-6 py-4 flex items-center gap-4 shrink-0">
          <div className="flex-1">
            <p className="text-[10px] uppercase font-semibold tracking-wider text-slate-400">Cross-property · applied to every property</p>
            <h2 className="font-display text-xl font-bold text-slate-900">Taxation</h2>
          </div>
          <button data-testid="tax-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        {!cfg ? (
          <div className="p-10 flex items-center justify-center"><Loader2 className="animate-spin text-slate-400" size={22} /></div>
        ) : (
          <div className="p-6 space-y-5 flex-1 overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-400">Country</label>
                <select data-testid="tax-country" value={cfg.country || "IN"} onChange={(e) => {
                  const c = COUNTRIES.find((x) => x.code === e.target.value);
                  setCfg({ ...cfg, country: c.code, currency: c.currency });
                }} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                  {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-400">Currency</label>
                <input value={cfg.currency || "INR"} readOnly className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-slate-50" />
              </div>
            </div>
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 text-[11px] text-indigo-900">
              <b>India (hospitality) reference — verified 22 Sep 2025:</b> Nil GST below ₹1,000/night, 5% for ₹1,000–₹7,500, 18% above ₹7,500 (per unit per day).
              <button onClick={applyIndiaPreset} className="ml-2 underline font-semibold">Apply this preset</button>
            </div>
            <div>
              <p className="text-[10px] uppercase font-semibold tracking-wider text-slate-400 mb-2">Slabs · nightly tariff → tax %</p>
              <div className="space-y-2">
                {(cfg.slabs || []).map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input type="number" placeholder="Min (₹)" value={s.min ?? ""} onChange={(e) => updateSlab(i, "min", e.target.value)} className="w-28 border border-slate-300 rounded-lg px-2 py-1.5 text-sm" />
                    <span className="text-xs text-slate-400">to</span>
                    <input type="number" placeholder="Max (blank = ∞)" value={s.max ?? ""} onChange={(e) => updateSlab(i, "max", e.target.value)} className="w-36 border border-slate-300 rounded-lg px-2 py-1.5 text-sm" />
                    <span className="text-xs text-slate-400">→</span>
                    <input type="number" step="0.1" placeholder="Rate %" value={s.rate ?? ""} onChange={(e) => updateSlab(i, "rate", e.target.value)} className="w-24 border border-slate-300 rounded-lg px-2 py-1.5 text-sm" />
                    <span className="text-xs text-slate-400">%</span>
                    <button onClick={() => removeSlab(i)} className="text-slate-400 hover:text-red-500 ml-auto"><X size={14} /></button>
                  </div>
                ))}
              </div>
              <button onClick={addSlab} className="text-xs text-[#4F46E5] font-semibold mt-2">+ Add slab</button>
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
              <button onClick={onClose} className="text-sm text-slate-500 hover:text-slate-800 px-4 py-2">Cancel</button>
              <button data-testid="tax-save" onClick={save} disabled={busy}
                className="flex items-center gap-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-full px-5 py-2 disabled:opacity-50">
                {busy ? <Loader2 className="animate-spin" size={13} /> : <Save size={13} />} Apply to every property
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </Portal>
  );
}

// Phase 13 (2026-02): Health Center — cross-property OTA Connectivity drift +
// sync log. Pulls the same /sync-log endpoint the Integrations dialog uses
// but WITHOUT a pid filter so admins see every property in one place.
function HealthCenterDialog({ onClose }) {
  const [rows, setRows] = useState(null);
  const [drift, setDrift] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/pms/integrations/sync-log?limit=100").then(({ data }) => setRows(data))
      .catch(() => setRows({ webhooks: [], sync_jobs: [] }));
  }, []);
  const runReconcile = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/api/cron/atiti-connect-reconcile", null, { baseURL: process.env.REACT_APP_BACKEND_URL, headers: { "x-webhook-id": `admin-${Date.now()}` } });
      setDrift(data);
      toast.success(`Reconcile complete · ${data.drift_count} issue${data.drift_count !== 1 ? "s" : ""} found`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Portal>
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[80] grid place-items-center p-3 sm:p-4 md:p-6 overflow-y-auto pt-safe pb-safe pl-safe pr-safe" onClick={onClose}>
        <div className="bg-white rounded-2xl w-[96vw] min-h-[92vh] max-h-[92vh] max-w-none shadow-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="health-center-dialog">
          <div className="px-5 md:px-8 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="font-display font-bold text-slate-900">Connectivity Health</h2>
              <p className="text-[11px] text-slate-500">Cross-property OTA Connectivity sync log &amp; drift</p>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="run-reconcile-btn" onClick={runReconcile} disabled={busy}
                className="btn-brand rounded-full px-4 py-2 text-xs font-semibold disabled:opacity-50">
                {busy ? "Reconciling…" : "Run reconcile now"}
              </button>
              <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500">✕</button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-5 md:p-8 space-y-4">
            <MarginWidget />
            {drift && (
              <div className={`rounded-xl border px-4 py-3 text-xs ${drift.drift_count ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`}>
                <p className="font-semibold">{drift.drift_count ? `${drift.drift_count} drift issue(s) found` : "No drift — every mapping matches the upstream state."}</p>
                {(drift.drift || []).slice(0, 5).map((d, i) => (
                  <p key={i} className="mt-1 text-[11px] font-mono">{d.kind} · property {String(d.property_id).slice(0, 8)} {d.unit_id ? `· unit ${String(d.unit_id).slice(0, 8)}` : ""} {d.detail || ""}</p>
                ))}
              </div>
            )}
            {rows === null && <p className="text-xs text-slate-400">Loading…</p>}
            {rows && (rows.webhooks.length + rows.sync_jobs.length === 0) && (
              <p className="text-xs text-slate-400">No inbound webhooks or outbound pushes across any property yet. This surface fills up automatically once your first reservation flows through OTA Connectivity.</p>
            )}
            {rows && (rows.webhooks.length + rows.sync_jobs.length > 0) && (
              <div>
                <h3 className="font-display font-bold text-slate-900 text-sm mb-2">Recent events (all properties)</h3>
                <ul className="space-y-1.5 text-[11px]">
                  {rows.sync_jobs.map((j) => (
                    <li key={j.id} className="flex gap-3 items-baseline">
                      <span className="text-slate-400 font-mono w-28 shrink-0">{new Date(j.created_at).toLocaleString()}</span>
                      <span className={`inline-block w-16 shrink-0 font-semibold ${j.rate_result?.ok && j.availability_result?.ok ? "text-emerald-600" : "text-amber-600"}`}>push</span>
                      <span className="text-slate-600">prop {String(j.property_id).slice(0, 8)} · {j.count}n · rate {j.rate_result?.ok ? "✓" : "✗"} · avail {j.availability_result?.ok ? "✓" : "✗"}</span>
                    </li>
                  ))}
                  {rows.webhooks.map((w) => (
                    <li key={w.id} className="flex gap-3 items-baseline">
                      <span className="text-slate-400 font-mono w-28 shrink-0">{new Date(w.received_at).toLocaleString()}</span>
                      <span className={`inline-block w-16 shrink-0 font-semibold ${w.status === "processed" ? "text-emerald-600" : w.status === "error" ? "text-rose-600" : "text-slate-500"}`}>in · {w.event_type}</span>
                      <span className="text-slate-600 truncate">{w.error || w.status}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
}

// Phase 14 (2026-02): OTA Connectivity margin widget — compares provider cost
// (what Atiti pays upstream) vs Atiti's PMS subscription revenue on the
// same set of properties. Shown at the top of the Health Center so admins
// can price-check every host at a glance.
function MarginWidget() {
  const [m, setM] = useState(null);
  useEffect(() => {
    api.get("/pms/integrations/margin").then(({ data }) => setM(data)).catch(() => {});
  }, []);
  const download = async (path, filename) => {
    try {
      const { data } = await api.get(path);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded");
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!m) return null;
  const marginPositive = m.margin_usd_monthly >= 0;
  return (
    <div className="bg-gradient-to-br from-indigo-50 to-white border border-indigo-100 rounded-2xl p-5" data-testid="margin-widget">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-wider font-bold text-indigo-700">OTA Connectivity margin</p>
          <p className={`font-display font-black text-3xl mt-1 ${marginPositive ? "text-emerald-600" : "text-rose-600"}`}>
            {marginPositive ? "+" : ""}${m.margin_usd_monthly.toFixed(2)}<span className="text-sm font-normal text-slate-500">/mo</span>
          </p>
          <p className="text-[11px] text-slate-500 mt-1">Revenue ${m.subscription_revenue_usd_monthly}/mo · Cost ${m.provider_cost_usd_monthly}/mo · {m.properties} property(ies) · {m.fx_note}</p>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <button data-testid="cert-provider-info" onClick={() => download("/pms/integrations/cert/provider-info", "atiti-connect-provider-info.json")}
            className="text-xs px-3 py-1.5 rounded-full border border-slate-300 hover:border-slate-500 whitespace-nowrap">Provider info (JSON)</button>
          <button data-testid="cert-provider-pdf" onClick={async () => {
            try {
              const res = await api.get("/pms/integrations/cert/provider-info.pdf", { responseType: "blob" });
              const url = URL.createObjectURL(res.data);
              const a = document.createElement("a"); a.href = url; a.download = "atiti-connect-cert-packet.pdf"; a.click();
              URL.revokeObjectURL(url);
              toast.success("Signed cert packet downloaded");
            } catch (e) { toast.error(errMsg(e)); }
          }}
            className="text-xs px-3 py-1.5 rounded-full btn-brand text-white whitespace-nowrap font-semibold">Signed PDF cert packet</button>
          <button data-testid="cert-audit-export" onClick={() => download("/pms/integrations/cert/audit-export?days=90", "atiti-connect-audit-90d.json")}
            className="text-xs px-3 py-1.5 rounded-full border border-slate-300 hover:border-slate-500 whitespace-nowrap">Audit export (90d)</button>
        </div>
      </div>
    </div>
  );
}
